import { useState } from "react";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import { submitContact } from "@/lib/api";
import { EMAIL } from "@/lib/site";
import { scrollToId } from "@/lib/scroll";
import { getVisitorId } from "@/lib/visitor";

const inputCls =
  "w-full border-b border-white/20 bg-transparent py-3 text-sm text-white placeholder-white/25 outline-none transition-colors focus:border-accent";
const labelCls = "font-mono text-[10px] tracking-[0.25em] text-fog";

export default function Contact() {
  const [state, setState] = useState("idle");

  async function onSubmit(e) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = Object.fromEntries(new FormData(form));
    data.visitor_id = getVisitorId();
    setState("sending");
    try {
      const res = await submitContact(data);
      if (res.data?.ok) {
        setState("sent");
        toast.success("Message sent — a confirmation is on its way to your inbox.");
        form.reset();
      } else {
        throw new Error("unexpected response");
      }
    } catch {
      setState("error");
      toast.error("Something broke in transit — email me directly instead.");
    }
  }

  return (
    <section id="contact" className="border-t border-white/10">
      <div className="mx-auto grid max-w-7xl gap-16 px-6 py-28 lg:grid-cols-2 lg:gap-24 lg:px-10 lg:py-36">
        <Reveal>
          <p className="font-mono text-[11px] tracking-[0.3em] text-fog">04 // CONTACT</p>
          <h2 className="mt-6 font-display text-4xl font-black tracking-tight text-white sm:text-5xl">
            One engagement.
            <br />
            Let's make it count.
          </h2>

          <a
            data-testid="contact-email-link"
            href={`mailto:${EMAIL}`}
            className="mt-14 block font-display text-2xl font-black tracking-tight text-white underline decoration-accent decoration-2 underline-offset-8 transition-colors hover:text-accent sm:text-3xl lg:text-4xl"
          >
            {EMAIL}
          </a>
          <p className="mt-6 font-mono text-[10px] tracking-[0.25em] text-fog">
            PREFER EMAIL? WRITE DIRECTLY — IT LANDS IN MY INBOX.
          </p>

          <div className="mt-14 inline-flex items-center gap-3 border border-white/15 px-4 py-2">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-75" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-accent" />
            </span>
            <span data-testid="contact-availability-badge" className="font-mono text-[10px] tracking-[0.25em] text-white/80">
              AVAILABLE FOR ONE CONTRACT ENGAGEMENT
            </span>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          {state === "sent" ? (
            <div data-testid="contact-success-note" className="flex h-full flex-col items-start justify-center border border-white/15 p-10">
              <p className="font-display text-2xl font-bold text-white">Message received.</p>
              <p className="mt-3 text-sm leading-relaxed text-fog">
                It's logged and a confirmation is on its way to your inbox. I'll
                reply from {EMAIL} as soon as I've read it.
              </p>
              <button
                data-testid="contact-send-another-button"
                onClick={() => setState("idle")}
                className="mt-8 border border-white/20 px-6 py-3 font-mono text-[11px] tracking-[0.2em] text-white transition-colors hover:border-white"
              >
                SEND ANOTHER
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} data-testid="contact-form" className="flex flex-col gap-8 pt-1">
              <div>
                <label htmlFor="cf-name" className={labelCls}>
                  NAME *
                </label>
                <input id="cf-name" name="name" data-testid="contact-name-input" required minLength={2} maxLength={100} placeholder="Ada Lovelace" className={inputCls} />
              </div>
              <div>
                <label htmlFor="cf-email" className={labelCls}>
                  EMAIL *
                </label>
                <input id="cf-email" name="email" type="email" data-testid="contact-email-input" required maxLength={200} placeholder="ada@ai-hardware.com" className={inputCls} />
              </div>
              <div>
                <label htmlFor="cf-company" className={labelCls}>
                  COMPANY
                </label>
                <input id="cf-company" name="company" data-testid="contact-company-input" maxLength={120} placeholder="Optional" className={inputCls} />
              </div>
              <div>
                <label htmlFor="cf-message" className={labelCls}>
                  MESSAGE *
                </label>
                <textarea
                  id="cf-message"
                  name="message"
                  data-testid="contact-message-input"
                  required
                  minLength={10}
                  maxLength={5000}
                  rows={5}
                  placeholder="What are you building, and where does the compiler hurt?"
                  className={`${inputCls} resize-none`}
                />
              </div>
              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={state === "sending"}
                className="group flex items-center justify-center gap-3 self-start bg-white px-10 py-4 font-mono text-[11px] tracking-[0.2em] text-ink transition-colors hover:bg-accent disabled:cursor-wait disabled:opacity-70"
              >
                {state === "sending" ? (
                  <>
                    SENDING <Loader2 className="h-4 w-4 animate-spin" />
                  </>
                ) : (
                  <>
                    SEND MESSAGE
                    <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </>
                )}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}
